import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Breadcrumbs from '../../components/ui/Breadcrumbs';
import TextInput from '../../components/ui/TextInput.jsx';
import CheckboxInput from '../../components/ui/CheckboxInput';
import Button from '../../components/ui/Button';
import LoadErrorBanner from '../../components/ui/LoadErrorBanner';
import { useTalentSettingsApi } from './config';

// Task 36 — TD Konfigürasyon sayfası (/talent/settings, ['TD.Write']).
// PerformanceSettingsPage (Task 33) İLE AYNI desen: bölüm kartları + ağırlık
// toplamı canlı sayacı (AYNI yeşil/kırmızı ton: #1B7A3E / #B02A37, Task 33'ün
// GoalsPage dip-toplam desenini birebir tekrarı). PM'in aksine tek sekme —
// bu sayfa yalnız GET/PUT v1/talent-settings'i (Task 1/24) tüketir, 360° form
// şablonu/bildirim şablonu benzeri alt-kaynak yok.

const Container = styled.div``;

const Section = styled.div`
  background: #fff;
  border: 1px solid #E8ECF0;
  border-radius: 6px;
  padding: 20px;
  margin-bottom: 18px;
`;

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  margin-bottom: 14px;
`;

const FlagsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 14px;
  margin-bottom: 14px;
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 16px;
`;

const HintText = styled.p`
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 14px;
`;

const TotalRow = styled.div`
  display: flex;
  align-items: center;
  padding-top: 4px;
`;

const TotalWeight = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${({ $balanced }) => ($balanced ? '#1B7A3E' : '#B02A37')};
`;

const FooterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  justify-content: flex-end;
`;

const SavedHint = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.auxiliar.success};
  font-weight: 600;
`;

const POT_WEIGHT_FIELDS = ['potWeightLearningAgility', 'potWeightLeadership', 'potWeightMotivation', 'potWeightImpact'];

export default function TalentSettingsPage() {
  const { t } = useTranslation();
  const { settings, fetching, saving, save, loadFailed } = useTalentSettingsApi();
  const [form, setForm] = useState(null);
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    if (settings) setForm(settings);
  }, [settings]);

  const setBoolField = (key) => (val) => setForm((prev) => ({ ...prev, [key]: val }));
  const setNumField = (key) => (e) => {
    const raw = e.target.value;
    setForm((prev) => ({ ...prev, [key]: raw === '' ? 0 : Number(raw) }));
  };

  const weightsTotal = useMemo(() => {
    if (!form) return 0;
    return POT_WEIGHT_FIELDS.reduce((sum, key) => sum + (Number(form[key]) || 0), 0);
  }, [form]);
  const weightsBalanced = weightsTotal === 100;

  const handleSave = async () => {
    if (!form || !weightsBalanced) return;
    const ok = await save(form);
    if (ok) {
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
    }
  };

  return (
    <Container>
      <Breadcrumbs items={[{ label: t('talent.settings.title') }]} />

      {/* TD-23 — ayarlar YÜKLENEMEDİYSE form HİÇ açılmaz. Eskiden `settings`
          null kalıyor, form da null kaldığı için ekran süresiz "Yükleniyor"da
          takılıyordu; kullanıcı hatanın ne olduğunu göremiyordu. Daha kötüsü:
          hook varsayılan bir nesne döndürmüş olsaydı KAYDET, kiracının gerçek
          eşiklerini varsayılanlarla ezerdi. */}
      {loadFailed ? (
        <LoadErrorBanner />
      ) : fetching || !form ? (
        <p>{t('common.loading')}</p>
      ) : (
        <>
          <Section>
            <SectionTitle>{t('talent.settings.thresholds.sectionTitle')}</SectionTitle>
            <HintText>{t('talent.settings.thresholds.hint')}</HintText>
            <FieldsGrid>
              <TextInput label={t('talent.settings.thresholds.perfLowMax')} type="number" step="0.01" value={form.nineBoxPerfLowMax} onChange={setNumField('nineBoxPerfLowMax')} />
              <TextInput label={t('talent.settings.thresholds.perfMidMax')} type="number" step="0.01" value={form.nineBoxPerfMidMax} onChange={setNumField('nineBoxPerfMidMax')} />
              <TextInput label={t('talent.settings.thresholds.potLowMax')} type="number" step="0.01" value={form.nineBoxPotLowMax} onChange={setNumField('nineBoxPotLowMax')} />
              <TextInput label={t('talent.settings.thresholds.potMidMax')} type="number" step="0.01" value={form.nineBoxPotMidMax} onChange={setNumField('nineBoxPotMidMax')} />
            </FieldsGrid>
            <FlagsGrid style={{ marginTop: 16, marginBottom: 0 }}>
              <CheckboxInput
                label={t('talent.settings.thresholds.employeeCanSeeNineBox')}
                checked={!!form.employeeCanSeeNineBox}
                onChange={setBoolField('employeeCanSeeNineBox')}
              />
            </FlagsGrid>
          </Section>

          <Section>
            <SectionTitle>{t('talent.settings.weights.sectionTitle')}</SectionTitle>
            <HintText>{t('talent.settings.weights.hint')}</HintText>
            <FieldsGrid>
              <TextInput label={t('talent.settings.weights.learningAgility')} type="number" value={form.potWeightLearningAgility} onChange={setNumField('potWeightLearningAgility')} />
              <TextInput label={t('talent.settings.weights.leadership')} type="number" value={form.potWeightLeadership} onChange={setNumField('potWeightLeadership')} />
              <TextInput label={t('talent.settings.weights.motivation')} type="number" value={form.potWeightMotivation} onChange={setNumField('potWeightMotivation')} />
              <TextInput label={t('talent.settings.weights.impact')} type="number" value={form.potWeightImpact} onChange={setNumField('potWeightImpact')} />
            </FieldsGrid>
            <TotalRow>
              <TotalWeight $balanced={weightsBalanced}>
                {t('talent.settings.weights.total')}: {weightsTotal}% / 100%
              </TotalWeight>
            </TotalRow>
          </Section>

          <Section>
            <SectionTitle>{t('talent.settings.idp.sectionTitle')}</SectionTitle>
            <FlagsGrid>
              <CheckboxInput
                label={t('talent.settings.idp.linkedToPeriod')}
                checked={!!form.idpLinkedToPeriod}
                onChange={setBoolField('idpLinkedToPeriod')}
              />
            </FlagsGrid>
            <FieldsGrid>
              <TextInput label={t('talent.settings.idp.draftSlaDays')} type="number" value={form.idpDraftSlaDays} onChange={setNumField('idpDraftSlaDays')} />
              <TextInput label={t('talent.settings.idp.meetingSlaDays')} type="number" value={form.idpMeetingSlaDays} onChange={setNumField('idpMeetingSlaDays')} />
              <TextInput label={t('talent.settings.idp.approvalSlaDays')} type="number" value={form.idpApprovalSlaDays} onChange={setNumField('idpApprovalSlaDays')} />
            </FieldsGrid>
          </Section>

          <Section>
            <SectionTitle>{t('talent.settings.succession.sectionTitle')}</SectionTitle>
            <FieldsGrid>
              <TextInput label={t('talent.settings.succession.reviewMonths')} type="number" value={form.successionReviewMonths} onChange={setNumField('successionReviewMonths')} />
            </FieldsGrid>
          </Section>

          <Section>
            <SectionTitle>{t('talent.settings.flightRisk.sectionTitle')}</SectionTitle>
            <FlagsGrid style={{ marginBottom: 0 }}>
              <CheckboxInput
                label={t('talent.settings.flightRisk.visibleToManager')}
                checked={!!form.flightRiskVisibleToManager}
                onChange={setBoolField('flightRiskVisibleToManager')}
              />
            </FlagsGrid>
          </Section>

          <FooterBar>
            {savedFlash && <SavedHint>{t('talent.settings.savedHint')}</SavedHint>}
            <Button onClick={handleSave} disabled={saving || !weightsBalanced} loading={saving}>
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </FooterBar>
        </>
      )}
    </Container>
  );
}
